/** @module Queries and mutations the admin screen shares; every call goes through the client. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type components, problemFrom } from "@/api/client";

export type SystemStatus = components["schemas"]["SystemStatusOut"];
export type Job = components["schemas"]["JobOut"];
export type Logs = components["schemas"]["LogsOut"];
export type TrainingStatus = components["schemas"]["TrainingStatusOut"];
export type Recognition = components["schemas"]["RecognitionStatusOut"];
export type Schedule = components["schemas"]["ScheduleOut"];
export type ScanHealth = components["schemas"]["ScanHealthOut"];
export type Quality = components["schemas"]["QualityOut"];

/** While something is running the screen should feel live, not archival. */
export const FAST_REFETCH = 4000;
export const SLOW_REFETCH = 20000;

export function useSystem() {
  return useQuery({
    queryKey: ["admin", "system"],
    queryFn: async (): Promise<SystemStatus> => {
      const { data, response } = await api.GET("/api/v1/admin/system", {});
      if (!data) throw problemFrom(response);
      return data;
    },
    refetchInterval: SLOW_REFETCH,
  });
}

export function useJobs(limit = 30) {
  return useQuery({
    queryKey: ["admin", "jobs", limit],
    queryFn: async (): Promise<Job[]> => {
      const { data, response } = await api.GET("/api/v1/admin/jobs", {
        params: { query: { limit } },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    refetchInterval: (query) =>
      (query.state.data ?? []).some((job) => job.status === "running" || job.status === "queued")
        ? FAST_REFETCH
        : SLOW_REFETCH,
  });
}

export function useSchedules() {
  return useQuery({
    queryKey: ["admin", "schedules"],
    queryFn: async (): Promise<Schedule[]> => {
      const { data, response } = await api.GET("/api/v1/admin/schedules", {});
      if (!data) throw problemFrom(response);
      return data;
    },
    refetchInterval: SLOW_REFETCH,
  });
}

export function useScanHealth() {
  return useQuery({
    queryKey: ["admin", "scans"],
    queryFn: async (): Promise<ScanHealth> => {
      const { data, response } = await api.GET("/api/v1/admin/scans", {});
      if (!data) throw problemFrom(response);
      return data;
    },
    refetchInterval: SLOW_REFETCH,
  });
}

export function useQuality() {
  return useQuery({
    queryKey: ["admin", "quality"],
    queryFn: async (): Promise<Quality> => {
      const { data, response } = await api.GET("/api/v1/admin/quality", {});
      if (!data) throw problemFrom(response);
      return data;
    },
  });
}

export function useRecognition() {
  return useQuery({
    queryKey: ["admin", "recognition"],
    queryFn: async (): Promise<Recognition> => {
      const { data, response } = await api.GET("/api/v1/admin/recognition", {});
      if (!data) throw problemFrom(response);
      return data;
    },
  });
}

export function useTraining(lines = 200) {
  return useQuery({
    queryKey: ["admin", "training", lines],
    queryFn: async (): Promise<TrainingStatus> => {
      const { data, response } = await api.GET("/api/v1/admin/training", {
        params: { query: { lines } },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    refetchInterval: (query) => (query.state.data?.running ? FAST_REFETCH : SLOW_REFETCH),
  });
}

export const LEVELS = ["debug", "info", "warning", "error", "critical"] as const;
export type Level = (typeof LEVELS)[number];

export interface LogQuery {
  service: string;
  lines: number;
  level?: Level;
  contains?: string;
  live: boolean;
}

export function useLogs(options: LogQuery) {
  const { service, lines, level, contains, live } = options;
  return useQuery({
    queryKey: ["admin", "logs", service, lines, level ?? "", contains ?? ""],
    queryFn: async (): Promise<Logs> => {
      const { data, response } = await api.GET("/api/v1/admin/logs", {
        params: {
          query: {
            service,
            lines,
            ...(level ? { level } : {}),
            ...(contains ? { contains } : {}),
          },
        },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    refetchInterval: live ? FAST_REFETCH : false,
  });
}

/** Everything the admin can trigger, by the name the button uses. */
export type Trigger =
  | { kind: "catalog"; mode: "incremental" | "full" }
  | { kind: "prices" }
  | { kind: "images"; quality: "low" | "high"; lang: string }
  | { kind: "index"; mode: "incremental" | "full" }
  | { kind: "benchmark" }
  | { kind: "backup" };

async function fire(trigger: Trigger): Promise<Job> {
  const call = async () => {
    switch (trigger.kind) {
      case "catalog":
        return api.POST("/api/v1/admin/sync/catalog", {
          params: { query: { mode: trigger.mode } },
        });
      case "prices":
        return api.POST("/api/v1/admin/sync/prices", {});
      case "images":
        return api.POST("/api/v1/admin/images/prefetch", {
          params: { query: { quality: trigger.quality, lang: trigger.lang } },
        });
      case "index":
        return api.POST("/api/v1/admin/index/rebuild", {
          params: { query: { mode: trigger.mode } },
        });
      case "benchmark":
        return api.POST("/api/v1/admin/recognition/calibrate", {
          params: { query: { source: "scans" } },
        });
      case "backup":
        return api.POST("/api/v1/admin/backup", {});
    }
  };
  const { data, response } = await call();
  if (!data) throw problemFrom(response);
  return data;
}

export function useTrigger() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fire,
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}

export function useCancelJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string): Promise<Job> => {
      const { data, response } = await api.POST("/api/v1/admin/jobs/{job_id}/cancel", {
        params: { path: { job_id: jobId } },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: ["admin", "jobs"] });
    },
  });
}

export function useTrainingControl() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (action: "start" | "stop"): Promise<TrainingStatus> => {
      const { data, response } = await (action === "start"
        ? api.POST("/api/v1/admin/training/start", {})
        : api.POST("/api/v1/admin/training/stop", {}));
      if (!data) throw problemFrom(response);
      return data;
    },
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: ["admin", "training"] });
    },
  });
}

export function useUpdateRecognition() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["RecognitionUpdate"]): Promise<Recognition> => {
      const { data, response } = await api.PATCH("/api/v1/admin/recognition", { body });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: ["admin", "recognition"] });
    },
  });
}
